import { useEffect, useRef, useState, type FormEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import http from "../../api/apiClient";
import { euro, monthLabel, type ContributionCell } from "./contributionsModel";

/**
 * Switch a single term of a child off (nothing due that month), or on again. For example a child that turns 4 at the
 * end of a month but only starts the month after. Separate from a different contribution, so both can apply.
 */
export function SkipTermDialog({ target, onClose, onSaved }: {
  target: { childId: string; childName: string; cell: ContributionCell } | null;
  onClose: () => void;
  onSaved: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const busy = useRef(false);

  useEffect(() => {
    setNote(target?.cell.skipNote ?? "");
    setMessage(null);
  }, [target]);

  const cell = target?.cell;
  const month = cell ? `${monthLabel(cell.month, "long")}` : "";
  const url = target ? `/finance/contributions/skipped-months/${target.childId}/${target.cell.month}` : "";

  const run = async (action: () => Promise<unknown>) => {
    if (busy.current) return;
    busy.current = true;
    setMessage(null);
    try {
      await action();
      onSaved();
    } catch {
      setMessage(t("finance.skip.error", { defaultValue: "Could not save. Try again." }));
    } finally {
      busy.current = false;
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void run(() => http.put(url, { note: note.trim() || null }));
  };

  const field = "h-10 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 dark:border-white/10 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-400/20";

  return (
    <Modal show={target !== null} onClose={onClose} hideDefaultActions showIcon={false}
      title={cell?.skipped
        ? t("finance.skip.on_title", { month, defaultValue: "Switch {{month}} on again?" })
        : t("finance.skip.off_title", { month, defaultValue: "Switch {{month}} off?" })}>
      <form onSubmit={submit} className="grid gap-4 text-left text-sm">
        <p className="-mt-2 text-center text-sm text-slate-500 dark:text-slate-400">{target?.childName}</p>
        <p className="text-slate-600 dark:text-slate-300">
          {cell?.skipped
            ? t("finance.skip.on_text", { defaultValue: "The normal amount is due again for this month." })
            : t("finance.skip.off_text", { defaultValue: "Nothing is due for this month. Parents see the month as free." })}
        </p>
        {!cell?.skipped && cell && Number(cell.paid) > 0 && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-400/10 dark:text-amber-200">
            {t("finance.skip.paid_hint", { amount: euro(cell.paid), defaultValue: "{{amount}} has already been paid for this month; it then counts towards other months." })}
          </p>
        )}
        <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} className={field}
          aria-label={t("finance.adjustments.note", { defaultValue: "Note (optional, only for finance)" })}
          placeholder={t("finance.skip.note_placeholder", { defaultValue: "Reason (optional, only for finance), e.g. starts in November" })} />

        {message && <p role="alert" className="text-sm font-semibold text-rose-600">{message}</p>}

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4 dark:border-white/10">
          {cell?.skipped && (
            <button type="submit"
              className="h-10 rounded-lg px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-white/5">
              {t("finance.skip.save_note", { defaultValue: "Save note" })}
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={onClose}
              className="h-10 rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5">
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            {cell?.skipped ? (
              <button type="button" onClick={() => void run(() => http.delete(url))}
                className="h-10 rounded-lg bg-indigo-600 px-5 text-sm font-bold text-white hover:bg-indigo-700">
                {t("finance.skip.switch_on", { defaultValue: "Switch on" })}
              </button>
            ) : (
              <button type="submit" className="h-10 rounded-lg bg-indigo-600 px-5 text-sm font-bold text-white hover:bg-indigo-700">
                {t("finance.skip.switch_off", { defaultValue: "Switch off" })}
              </button>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
}
